/**
 * @file PortalPageView.js
 * @description Component for displaying portal pages with navigation breadcrumbs
 */
import React, { useEffect, useRef, useState } from 'react';
import {
    useNavigate,
    useParams,
    useLocation,
    useNavigationType,
} from 'react-router-dom';
import { useAuthContext } from '../../store/Contexts';
import {
    readPortalPage,
    readScopedPortalPage,
} from '../../services/portalPageService';
import FreeformGrid from '../fluid-grid/FreeformGrid';
import Toast from '../ui/toasts/Toast';
import { logEvent } from '../../services/loggingService';
import { EventDataTypeEnum, LogEventType } from '../../utils/data/enums';
import Spinner from '../ui/spinners/Spinner';
import ViewLiveBanner from '../home/ViewLiveBanner';
import styles from './PortalPageView.module.scss';
import { ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';

const BACK_KEY = 'navigationStack:back'; // Navigation back stack
const FORWARD_KEY = 'navigationStack:forward'; // Navigation forward stack
const NAMES_KEY = 'navigationStack:names'; // Array of portal names + id for nav. stack reference
const VIEW_LIVE_MODE_KEY = 'viewLive:viewMode';

const PortalPageView = ({
    portalIdProp = null,
    onClose,
    navigationStack = null,
    isPreview = false,
    isViewLive = false,
    onShowPortalView,
    previewMode = null,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();
    const { portalId: routePortalId } = useParams();
    const [portalId, setPortalId] = useState(
        portalIdProp ?? routePortalId ?? null,
    );
    const { adminView } = useAuthContext();

    const [backStack, setBackStack] = useState(() => {
        try {
            const raw = sessionStorage.getItem(BACK_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [forwardStack, setForwardStack] = useState(() => {
        try {
            const raw = sessionStorage.getItem(FORWARD_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [breadcrumbs, setBreadcrumbs] = useState(() => {
        try {
            const raw = sessionStorage.getItem(NAMES_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });

    const [fetchedContainers, setFetchedContainers] = useState([]);
    const [fetchedMobileConfiguration, setFetchedMobileConfiguration] =
        useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [viewMode, setViewMode] = useState(() => {
        if (previewMode === 'mobile') {
            return 'mobile';
        }
        try {
            const savedMode = sessionStorage.getItem(VIEW_LIVE_MODE_KEY);
            return savedMode === 'mobile' ? 'mobile' : 'desktop';
        } catch {
            return 'desktop';
        }
    });

    const prevBackRef = useRef(backStack);
    const prevForwardRef = useRef(forwardStack);
    const prevPortalRef = useRef(null);

    const persistBack = arr => {
        try {
            sessionStorage.setItem(BACK_KEY, JSON.stringify(arr));
        } catch (e) {
            console.warn(e);
        }
    };
    const persistForward = arr => {
        try {
            sessionStorage.setItem(FORWARD_KEY, JSON.stringify(arr));
        } catch (e) {
            console.warn(e);
        }
    };
    const persistNames = arr => {
        try {
            sessionStorage.setItem(NAMES_KEY, JSON.stringify(arr));
        } catch (e) {
            console.warn(e);
        }
    };

    const getStateStack = () => {
        let s = [];
        if (navigationStack?.length > 0) {
            s = navigationStack;
        } else {
            s = location.state?.navigationStack;
        }
        return Array.isArray(s) ? s.map(Number) : null;
    };
    const getStateBreadcrumbs = () => {
        const b = location.state?.breadcrumbs;
        return Array.isArray(b) ? b : null;
    };
    const getHomeFromLocation = () => {
        const maybe =
            location.state?.selectedTabId ??
            location.state?.homePortalId ??
            null;
        if (maybe === null || maybe === undefined) return null;
        const n = parseInt(maybe, 10);
        return Number.isNaN(n) ? null : n;
    };

    const isAutoPlaceholder = (name, pid) =>
        typeof name === 'string' && name === `Portal ${pid}`;

    useEffect(() => {
        if (!isViewLive) {
            return;
        }
        sessionStorage.setItem(VIEW_LIVE_MODE_KEY, viewMode);
    }, [isViewLive, viewMode]);

    useEffect(() => {
        const id = parseInt(portalId, 10);
        if (Number.isNaN(id)) return;

        const stateStack = getStateStack();
        const homeId = getHomeFromLocation();

        const prevBack =
            prevBackRef.current && prevBackRef.current.length
                ? prevBackRef.current
                : backStack;
        const prevForward =
            prevForwardRef.current && prevForwardRef.current.length
                ? prevForwardRef.current
                : forwardStack;

        let newBack = [];
        let newForward = [...prevForward];

        if (stateStack) {
            newBack = stateStack.slice();
            if (homeId !== null && newBack[0] !== homeId) {
                newBack = [homeId, ...newBack];
            }
            newForward = [];
        } else {
            if (navigationType === 'PUSH') {
                newBack = prevBack.slice();
                if (newBack[newBack.length - 1] !== id) {
                    newBack = [...newBack, id];
                }
                newForward = [];
            } else if (navigationType === 'REPLACE') {
                newBack = prevBack.length
                    ? [...prevBack.slice(0, -1), id]
                    : [id];
            } else {
                const idxInPrevBack = prevBack.lastIndexOf(
                    id,
                    prevBack.length - 2,
                );
                if (idxInPrevBack !== -1) {
                    const popped = prevBack.slice(idxInPrevBack + 1);
                    newBack = prevBack.slice(0, idxInPrevBack + 1);
                    newForward = [...popped, ...prevForward];
                } else {
                    const idxInPrevF = prevForward.indexOf(id);
                    if (idxInPrevF !== -1) {
                        const toRestore = prevForward.slice(0, idxInPrevF + 1);
                        newBack = [...prevBack, ...toRestore];
                        newForward = prevForward.slice(idxInPrevF + 1);
                    } else {
                        const sBack = (() => {
                            try {
                                const raw = sessionStorage.getItem(BACK_KEY);
                                return raw ? JSON.parse(raw) : [];
                            } catch {
                                return [];
                            }
                        })();
                        if (sBack.length && sBack[sBack.length - 1] === id) {
                            newBack = sBack;
                        } else {
                            newBack = [...prevBack, id];
                        }
                    }
                }
                if (homeId !== null && newBack[0] !== homeId) {
                    newBack = [homeId, ...newBack];
                }
            }
        }

        if (newBack[newBack.length - 1] !== id) {
            newBack = [...newBack, id];
        }

        setBackStack(newBack);
        setForwardStack(newForward);
        persistBack(newBack);
        persistForward(newForward);

        const stateBreadcrumbs = getStateBreadcrumbs();
        if (stateBreadcrumbs && stateBreadcrumbs.length) {
            const normalized = newBack.map(pid => {
                const found = stateBreadcrumbs.find(b => b.portalId === pid);
                const name =
                    found && typeof found.portalName === 'string'
                        ? isAutoPlaceholder(found.portalName, pid)
                            ? pid === homeId
                                ? 'Home'
                                : ''
                            : found.portalName
                        : pid === homeId
                          ? 'Home'
                          : '';
                return {
                    portalId: pid,
                    portalName: name,
                };
            });
            setBreadcrumbs(normalized);
            persistNames(normalized);
        } else {
            const cachedNames = (() => {
                try {
                    const raw = sessionStorage.getItem(NAMES_KEY);
                    return raw ? JSON.parse(raw) : null;
                } catch {
                    return null;
                }
            })();
            const prepop = newBack.map(pid => {
                const match = cachedNames?.find(c => c.portalId === pid);
                const matchedName =
                    match && typeof match.portalName === 'string'
                        ? isAutoPlaceholder(match.portalName, pid)
                            ? ''
                            : match.portalName
                        : '';
                return {
                    portalId: pid,
                    portalName: pid === homeId ? 'Home' : matchedName,
                };
            });
            setBreadcrumbs(prepop);

            let cancelled = false;
            (async () => {
                try {
                    const bc = [];
                    for (let i = 0; i < newBack.length; i++) {
                        const pid = newBack[i];
                        const match = cachedNames?.find(
                            c => c.portalId === pid,
                        );
                        if (
                            match &&
                            typeof match.portalName === 'string' &&
                            !isAutoPlaceholder(match.portalName, pid)
                        ) {
                            bc.push({
                                portalId: match.portalId,
                                portalName: match.portalName,
                            });
                            continue;
                        }
                        try {
                            const payload = adminView
                                ? await readPortalPage(pid, false)
                                : await readScopedPortalPage(pid);
                            const vm = payload?.viewModels?.[0];
                            bc.push({
                                portalId: vm?.id ?? pid,
                                portalName:
                                    vm?.displayName ||
                                    vm?.name ||
                                    (pid === homeId ? 'Home' : ''),
                            });
                        } catch {
                            bc.push({
                                portalId: pid,
                                portalName: pid === homeId ? 'Home' : '',
                            });
                        }
                        if (cancelled) return;
                    }
                    if (!cancelled) {
                        setBreadcrumbs(bc);
                        persistNames(bc);
                    }
                } catch (e) {
                    console.error('Breadcrumb fetch error', e);
                    Toast.error(getText('msg_cfab262ff2cc'));
                }
            })();
            return () => {
                cancelled = true;
            };
        }

        prevBackRef.current = newBack;
        prevForwardRef.current = newForward;
        prevPortalRef.current = id;
    }, [portalId, location.key, location.pathname, navigationType]);

    async function loadPortalPage(id) {
        if (!id) return;
        let fetchedPortalPage = {};
        try {
            setIsLoading(true);
            fetchedPortalPage = adminView
                ? await readPortalPage(id, true)
                : await readScopedPortalPage(id);
            if (fetchedPortalPage?.viewModels?.[0]?.isChild && !isViewLive) {
                logEvent(
                    LogEventType.NavigatedToPage,
                    `Visited child portal page ${fetchedPortalPage?.viewModels?.[0]?.name}`,
                    {
                        subjectDataTypeId: EventDataTypeEnum.Portal,
                        subjectId: id,
                        subjectName: fetchedPortalPage?.viewModels?.[0]?.name,
                    },
                );
            }
        } catch (e) {
            console.error(e);
            Toast.error(getText('msg_eeceb57efc21'));
        } finally {
            setFetchedContainers(fetchedPortalPage.containers || []);
            setFetchedMobileConfiguration(
                fetchedPortalPage?.viewModels?.[0]?.mobileConfiguration || [],
            );
            setIsLoading(false);
        }
    }

    useEffect(() => {
        const id = parseInt(portalId, 10);
        if (!Number.isNaN(id)) loadPortalPage(id);
    }, [portalId]);

    useEffect(() => {
        const nextPortalId =
            portalIdProp !== null && portalIdProp !== undefined
                ? portalIdProp
                : (routePortalId ?? null);

        setPortalId(nextPortalId);
    }, [portalIdProp, routePortalId]);

    const updateNavigationStacks = (newBack, newForward) => {
        setBackStack(newBack);
        setForwardStack(newForward);
        persistBack(newBack);
        persistForward(newForward);
    };

    const navigateToHome = target => {
        const newBack = [target];
        const newForward = [];
        updateNavigationStacks(newBack, newForward);
        persistNames([
            {
                portalId: target,
                portalName: breadcrumbs[0]?.portalName ?? 'Home',
            },
        ]);

        if (typeof onClose === 'function') {
            onClose();
            return;
        }

        const homeRoute = isViewLive
            ? ROUTE_BUILDERS.viewLivePortal(target)
            : ROUTE_BUILDERS.portalPagesView(target);
        navigate(homeRoute, {
            state: { selectedTabId: target },
        });
    };

    const calculateNewStacks = (target, backStackIndex) => {
        const curBack = backStack.slice();
        const idx =
            backStackIndex !== undefined
                ? backStackIndex
                : curBack.lastIndexOf(target);
        const { newBack, removed } =
            idx !== -1
                ? {
                      newBack: curBack.slice(0, idx + 1),
                      removed: curBack.slice(idx + 1),
                  }
                : {
                      newBack: [...curBack, target],
                      removed: [],
                  };

        const newForward = removed.length
            ? [...removed, ...forwardStack]
            : forwardStack.slice();

        return { newBack, newForward };
    };

    const handleNavigationClick = (portal, breadcrumbIndex) => {
        const target = parseInt(portal, 10);
        const current = parseInt(portalId, 10);
        if (target === current && breadcrumbIndex === breadcrumbs.length - 1)
            return;

        if (breadcrumbIndex === 0) {
            navigateToHome(target);
            return;
        }

        const { newBack, newForward } = calculateNewStacks(
            target,
            breadcrumbIndex,
        );
        updateNavigationStacks(newBack, newForward);

        const bcToStore =
            breadcrumbIndex !== undefined
                ? breadcrumbs.slice(0, breadcrumbIndex + 1)
                : undefined;

        if (isPreview) {
            if (bcToStore) {
                setBreadcrumbs(bcToStore);
                persistNames(bcToStore);
            }
            setPortalId(target);
            if (typeof onShowPortalView === 'function') {
                onShowPortalView(target, newBack);
            }
            return;
        }

        const navRoute = isViewLive
            ? ROUTE_BUILDERS.viewLivePortal(target)
            : ROUTE_BUILDERS.portalPagesView(target);
        navigate(navRoute, {
            state: {
                navigationStack: newBack,
                ...(newForward.length ? { forwardStack: newForward } : {}),
                ...(bcToStore ? { breadcrumbs: bcToStore } : {}),
            },
        });
    };

    const effectivePreviewMode = isViewLive ? viewMode : previewMode;
    const isMobilePreview =
        (isPreview || isViewLive) && effectivePreviewMode === 'mobile';

    return (
        <>
            <div
                className={
                    isMobilePreview
                        ? styles.breadcrumbContainerMobilePreview
                        : styles.breadcrumbContainer
                }
            >
                <div
                    className={styles.breadcrumbScroller}
                    style={
                        isMobilePreview
                            ? { justifyContent: 'flex-start' }
                            : undefined
                    }
                >
                    {breadcrumbs.map((portal, index) => {
                        const isCurrent = index === breadcrumbs.length - 1;
                        return (
                            <div key={`${portal.portalId}-${index}`}>
                                <span
                                    className={styles.breadcrumbItem}
                                    onClick={() =>
                                        handleNavigationClick(
                                            portal.portalId,
                                            index,
                                        )
                                    }
                                    style={{
                                        color: isCurrent
                                            ? '#1366FF'
                                            : '#667085',
                                        fontWeight: isCurrent ? '600' : '400',
                                        cursor: isCurrent ? 'auto' : 'pointer',
                                    }}
                                    title={portal.portalName || 'Loading...'}
                                >
                                    {portal.portalName === ''
                                        ? '\u00A0'
                                        : portal.portalName}
                                </span>
                                {index < breadcrumbs.length - 1 && (
                                    <i
                                        className="bi bi-chevron-right"
                                        style={{
                                            fontSize: '0.8em',
                                            marginLeft: '10px',
                                            marginRight: '10px',
                                            marginTop: '3px',
                                            color: '#667085',
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {isLoading ? (
                <div className="tabLoadingIndicator">
                    <Spinner className={styles.loadingSpinner} />
                </div>
            ) : (
                <FreeformGrid
                    fetchedContainers={fetchedContainers}
                    fetchedMobileConfiguration={fetchedMobileConfiguration}
                    navigationStack={backStack}
                    isPreview={isPreview}
                    isViewLive={isViewLive}
                    onShowPortalView={onShowPortalView}
                    previewMode={effectivePreviewMode}
                />
            )}

            {isViewLive && (
                <>
                    {viewMode !== 'mobile' && (
                        <div style={{ height: '84px' }} />
                    )}
                    <ViewLiveBanner
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                    />
                </>
            )}
        </>
    );
};

export default PortalPageView;
